import { useEffect, useMemo, useState } from "react";

export interface UseTablePaginationOptions {
  initialRowsPerPage?: number;
}

/** rowsPerPage divides totalItems and bounds the slice, so 0/negative/NaN
 *  would poison totalPages (Infinity/NaN) and the start/end indices. */
const sanitizeRowsPerPage = (value: number | undefined, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : fallback;

/**
 * Page/rowsPerPage state for client-side-sliced tables, replacing the
 * copy-pasted boilerplate: rows-per-page change resets to page 0, and the
 * page snaps back to 0 if it falls past the end (e.g. after filtering).
 * Not for server-paginated lists whose rowsPerPage drives a request param.
 */
export function useTablePagination(totalItems: number, options?: UseTablePaginationOptions) {
  const initialRows = sanitizeRowsPerPage(options?.initialRowsPerPage, 10);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(initialRows);

  const totalPages = Math.ceil(totalItems / rowsPerPage);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPage) {
      setPage(0);
    }
  }, [totalItems, rowsPerPage, page]);

  const setRowsPerPage = (value: number) => {
    setRowsPerPageState(sanitizeRowsPerPage(value, initialRows));
    setPage(0);
  };

  const { start, end } = useMemo(
    () => ({ start: page * rowsPerPage, end: page * rowsPerPage + rowsPerPage }),
    [page, rowsPerPage],
  );

  return { page, rowsPerPage, totalPages, setPage, setRowsPerPage, start, end };
}
